import {
  Address,
  AddressWithType,
  Stop,
} from '@/api/freedomQl/__generated__/graphql';
import {
  ILoqateAddressResponse,
  ILoqateFindResponse,
  ILoqateGeoLocationResponse,
} from '@/services/LoqateAddressSearch';
import {
  extractIataCode,
  haversineFormula,
  toCoordinate,
  toNum,
} from './helpers';

import { AddressType } from '@/config/constants';
import { type IntegraAddress } from '@/api/integraLink/types';
import { Populate3waDetailsResponse } from '@/api/integraLink/types';
import { t } from './i18n';
import {
  GooglePlacesAddressResponse,
  GooglePlacesFindResponse,
  GooglePlacesGeoLocationResponse,
} from '@/services/googlePlaces';
import { HereGeoLocationResponse } from '@/services/hereGeocoding';

export const loqateAddressToAddress = (
  address: ILoqateAddressResponse,
): AddressWithType => {
  const compressedAddress = compressAddress(address);

  return {
    addressName: compressedAddress.Company,
    address1: compressedAddress.Line1,
    address2: compressedAddress.Line2,
    address3: compressedAddress.City,
    address4: compressedAddress.Province,
    postcode: compressedAddress.PostalCode,
    country: compressedAddress.CountryName,
    countryCode: compressedAddress.CountryIso2,
    lat: toCoordinate(compressedAddress.Field1),
    long: toCoordinate(compressedAddress.Field2),
    uuid: compressedAddress.Id,
    reference: '',
    typeCode: AddressType.ADDRESS,
  };
};

export const googlePlacesToAddress = (
  address: GooglePlacesAddressResponse,
): AddressWithType => {
  return {
    addressName: address.placeName,
    address1: address.line1,
    address2: address.line2,
    address3: address.city,
    address4: address.province,
    postcode: address.postalCode,
    country: address.countryName,
    countryCode: address.countryIso2,
    lat: toCoordinate(address.latitude),
    long: toCoordinate(address.longitude),
    uuid: address.placeId,
    reference: '',
    typeCode: address.addressTypeOverride ?? AddressType.POINT_OF_INTEREST,
    code:
      address.addressTypeOverride === AddressType.AIRPORT
        ? extractIataCode(address.placeName)
        : undefined,
  };
};

// HERE formats the label in the address convention of its country, so its
// first segment is the street line whichever order that country puts the
// house number and street in.
export const hereAddressLines = (
  address: HereGeoLocationResponse,
): { line1: string; rest: string } => {
  const [line1, ...rest] = address.address.label.split(', ');
  return { line1, rest: rest.join(', ') };
};

export const hereToAddress = (
  address: HereGeoLocationResponse,
): AddressWithType => {
  return {
    addressName: '',
    address1: hereAddressLines(address).line1,
    address2: '',
    address3: address.address.city ?? '',
    address4: address.address.state ?? '',
    postcode: address.address.postalCode ?? '',
    country: address.address.countryName ?? '',
    countryCode: address.countryInfo?.alpha2 ?? '',
    lat: toCoordinate(address.position.lat),
    long: toCoordinate(address.position.lng),
    uuid: address.id,
    reference: '',
    typeCode: AddressType.ADDRESS,
  };
};

export const w3wAddressToAddress = (
  address: Populate3waDetailsResponse,
): AddressWithType => {
  return {
    ...address,
    uuid: '',
    reference: '',
  };
};

const compressAddress = (
  address: ILoqateAddressResponse,
): ILoqateAddressResponse => {
  if (!address.Line4 && !address.Line5) {
    return address;
  }

  address.Line2 = `${address.Line2}, ${address.Line3}`;
  address.Line3 = address.Line4;
  address.Line4 = address.Line5;
  address.Line5 = '';

  if (!address.Line4) {
    return address;
  }

  address.Line2 = `${address.Line2}, ${address.Line3}`;
  address.Line3 = address.Line4;
  address.Line4 = '';

  return address;
};

export const addressParts = (address: AddressWithType | Stop) => {
  if ('asDirected' in address && address.asDirected) {
    return [t('form.stops.asDirectedLabel')];
  }

  return [
    address.addressName ?? '',
    address.address1 ?? '',
    address.address2 ?? '',
    address.address3 ?? '',
    address.address4 ?? '',
    address.postcode ?? '',
  ].filter((i) => i);
};

export const loqateGeolocationToFindResponse = (
  geo: ILoqateGeoLocationResponse,
  location?: { lat: number; long: number },
): ILoqateFindResponse => {
  const DistanceMeters = location
    ? haversineFormula(
        location.lat,
        location.long,
        toNum(geo.Latitude),
        toNum(geo.Longitude),
      )
    : undefined;

  return {
    Id: geo.Id,
    Type: geo.Type,
    Text: geo.Text,
    Highlight: '',
    Description: geo.Description,
    DistanceMeters,
  };
};

export const placesGeolocationToFindResponse = (
  geo: GooglePlacesGeoLocationResponse,
  location?: { lat: number; long: number },
): GooglePlacesFindResponse => {
  const distanceMeters = location
    ? haversineFormula(
        location.lat,
        location.long,
        toNum(geo.latitude),
        toNum(geo.longitude),
      )
    : undefined;

  return {
    placeId: geo.placeId,
    text: '',
    mainText: geo.text,
    secondaryText: geo.description,
    types: [],
    distanceMeters,
  };
};

export const addTypeToAddress = (
  address?: Address | null,
): AddressWithType | null => {
  if (!address) return null;
  const { type, ...rest } = address;
  return { ...rest, typeCode: type?.code ?? null } as AddressWithType;
};

export const addressWithTypeToIntegraAddress = (
  data: AddressWithType,
): IntegraAddress => {
  return {
    code: data.code ?? '',
    name: data.addressName ?? '',
    add1: data.address1 ?? '',
    add2: data.address2 ?? '',
    add3: data.address3 ?? '',
    add4: data.address4 ?? '',
    postcode: data.postcode ?? '',
    notes: data.notes ?? '',
    country: data.country ?? '',
    countryCode: data.countryCode ?? '',
    lat: data.lat ?? 0,
    long: data.long ?? 0,
    gridX: data.gridX,
    gridY: data.gridY,
  };
};
